class BbdProductStickyBar extends HTMLElement {
  /**
   * Constructs a new instance of the sticky bar component.
   *
   * - Initializes settings for element visibility, container, and add-to-cart button selectors.
   * - Binds the `toggleStickyBarVisibility` method to the instance.
   * - Subscribes to the `variantChange` event using `PUB_SUB_EVENTS`.
   * - Calls `initialize()` to set up the component.
   *
   * @constructor
   */
  constructor() {
    super();

    // Settings
    this.settings = {
      partiallyVisible: true,
      elementQueryToCheck: this.dataset.element,
      containerElementQuery: '.bbd-product-sticky-bar__container',
      addToCartElementQuery: '.bbd-product-sticky-bar__add-to-cart'
    };

    this.toggleStickyBarVisibility = this.toggleStickyBarVisibility.bind(this);

    // Subscribe to the variant change event
    /* eslint-disable no-undef */
    this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
    /* eslint-enable no-undef */

    this.initialize();
  }

  /**
   * Initializes the sticky bar component by selecting relevant DOM elements,
   * setting the initial visibility state of the sticky bar, and adding scroll listeners.
   *
   * This method:
   * - Selects `elementToCheck` to monitor its visibility in the viewport.
   * - Selects `containerElement` representing the sticky bar container.
   * - Selects `addToCartButton` to control its visibility.
   * - Calls `toggleStickyBarVisibility()` to set the initial state.
   * - Calls `addScrollListeners()` to monitor scroll events for visibility toggling.
   *
   * @returns {void}
   */
  initialize() {
    this.elementToCheck = document.querySelector(this.settings.elementQueryToCheck);
    this.containerElement = document.querySelector(this.settings.containerElementQuery);
    this.addToCartButton = document.querySelector(this.settings.addToCartElementQuery);

    this.toggleStickyBarVisibility();
    this.addScrollListeners();
  }

  /**
   * Adds a throttled scroll event listener to toggle the visibility of the sticky bar.
   *
   * The listener applies a throttle of 50ms to prevent excessive calls to `toggleStickyBarVisibility()`.
   * It ensures performance efficiency during scroll events.
   *
   * @returns {void}
   */
  addScrollListeners() {
    /* eslint-disable no-undef */
    window.addEventListener('scroll', throttle(this.toggleStickyBarVisibility, 50));
    /* eslint-enable no-undef */
  }

  /**
   * Handles the variant change event and updates the sticky bar content.
   *
   * Calls `loadNewStickyBar()` with the provided HTML content from the event data.
   *
   * @param {Object} event - The event object containing variant data.
   * @param {Object} event.data - The data object containing the HTML to update the sticky bar.
   * @param {DocumentFragment|HTMLElement} event.data.html - The HTML content to replace the sticky bar.
   *
   * @returns {void}
   */
  handleVariantChange({ data }) {
    this.loadNewStickyBar(data.html);
  }

  /**
   * Toggles the visibility of the sticky bar.
   *
   * Adds or removes the 'visible' class based on the result of `isElementInView()`.
   * The sticky bar becomes visible when `isElementInView()` returns `false`.
   *
   * @returns {void}
   */
  toggleStickyBarVisibility() {
    this.classList.toggle('visible', !this.isElementInView());
  }

  /**
   * Toggles the visibility of the "Add to Cart" button.
   *
   * Adds or removes the 'visible' class on the button element based on the `visible` parameter.
   *
   * @param {boolean} visible - Determines whether the button should be visible.
   * @returns {void}
   */
  toggleAddToCartButtonVisibility(visible) {
    this.addToCartButton.classList.toggle('visible', visible);
  }

  /**
   * Determines whether the target element is in the viewport.
   *
   * Checks if the element is either partially or fully visible based on the
   * `partiallyVisible` setting.
   *
   * @returns {boolean} True if the element is in view, false otherwise.
   */
  isElementInView() {
    const { top, left, bottom, right } = this.elementToCheck.getBoundingClientRect();
    const { innerHeight, innerWidth } = window;

    return this.settings.partiallyVisible
      ? ((top > 0 && top < innerHeight) || (bottom > 0 && bottom < innerHeight)) &&
          ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth))
      : top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
  }

  /**
   * Replaces the content of the sticky bar with new HTML content and reinitializes the component.
   *
   * @param {Document} html - The HTML document containing the new sticky bar content.
   * @returns {void}
   */
  loadNewStickyBar(html) {
    if (html) {
      const newStickyBarContent = html.querySelector(this.settings.containerElementQuery);

      if (newStickyBarContent) {
        this.containerElement.innerHTML = newStickyBarContent.innerHTML;
        this.initialize();
        this.toggleAddToCartButtonVisibility(true);
      }
    }
  }
}

customElements.define('bbd-product-sticky-bar', BbdProductStickyBar);
